import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import type { ReactNode } from "react";
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

export type PillTone = "success" | "warning" | "danger" | "teal" | "neutral";

const TONE: Record<PillTone, { bg: string; fg: string; dot?: string }> = {
  success: { bg: c.successBg, fg: c.successText, dot: c.successDot },
  warning: { bg: c.warnBg, fg: c.pillWarnText, dot: c.warnDot },
  danger: { bg: c.dangerBg, fg: c.pillDangerText, dot: c.dangerText },
  teal: { bg: c.pillTealBg, fg: c.pillTealText, dot: c.teal },
  neutral: { bg: c.pillNeutralBg, fg: c.pillNeutralText },
};

/** Maps a Jira/record status string → a tone, per the design's pill() map. */
export function statusTone(status: string): PillTone {
  const s = status.toLowerCase();
  if (["active", "done", "with client", "delivered", "audited", "reviewed"].includes(s)) return "success";
  if (["in progress", "in-progress", "onboarding", "charter"].includes(s)) return "warning";
  if (["stale", "overdue", "blocked", "churned"].includes(s)) return "danger";
  if (["quoting", "open", "ready"].includes(s)) return "teal";
  return "neutral";
}

/** Rounded pill chip mapping status → tinted bg/text (green/amber/red/teal). */
export function StatusPill({
  label,
  tone,
  status,
  dot,
  sx,
}: {
  label?: ReactNode;
  tone?: PillTone;
  /** If given, tone + label default from statusTone(status). */
  status?: string;
  dot?: boolean;
  sx?: SxProps<Theme>;
}) {
  const resolvedTone = tone ?? (status ? statusTone(status) : "neutral");
  const t = TONE[resolvedTone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: dot ? "6px" : 0,
        background: t.bg,
        color: t.fg,
        fontSize: 12,
        fontWeight: 600,
        borderRadius: `${brainTokens.radius.pill}px`,
        padding: "3px 11px",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {dot && t.dot && (
        <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: t.dot, flex: "none" }} />
      )}
      {label ?? status}
    </Box>
  );
}
